import { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal from '../components/Modal';
import Table, { Column } from '../components/Table';

interface Room {
  id: string;
  code: string;
  name: string;
  capacity: number;
  location: string;
  facilities: string;
}

export default function MeetingRooms() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', capacity: '', location: '', facilities: '' });
  const [bookingRoom, setBookingRoom] = useState<Room | null>(null);
  const [bookingForm, setBookingForm] = useState({ bookingDate: '', startTime: '', endTime: '', purpose: '', attendees: '' });
  const [myBookings, setMyBookings] = useState<any[]>([]);

  const fetchRooms = async () => {
    const res = await api.get('/hr/meeting-rooms');
    setRooms(res.data.rooms || []);
  };

  const fetchMyBookings = async () => {
    const res = await api.get('/hr/meeting-rooms/bookings/my');
    setMyBookings(res.data.bookings || []);
  };

  useEffect(() => {
    fetchRooms();
    fetchMyBookings();
  }, []);

  const createRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/hr/meeting-rooms', { ...form, capacity: form.capacity ? Number(form.capacity) : null });
      setForm({ code: '', name: '', capacity: '', location: '', facilities: '' });
      setShowForm(false);
      fetchRooms();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create room');
    }
  };

  const book = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bookingRoom) return;
    try {
      await api.post(`/hr/meeting-rooms/${bookingRoom.id}/bookings`, bookingForm);
      setBookingRoom(null);
      setBookingForm({ bookingDate: '', startTime: '', endTime: '', purpose: '', attendees: '' });
      fetchMyBookings();
      alert('Room booked');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to book room');
    }
  };

  const cancelBooking = async (id: string) => {
    await api.put(`/hr/meeting-rooms/bookings/${id}/cancel`);
    fetchMyBookings();
  };

  const columns: Column<Room>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name', sortable: true },
    { key: 'capacity', header: 'Capacity' },
    { key: 'location', header: 'Location' },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <button className="text-blue-600 hover:underline text-sm" onClick={() => setBookingRoom(r)}>
          Book
        </button>
      ),
    },
  ];

  const bookingColumns: Column<any>[] = [
    { key: 'room_name', header: 'Room' },
    { key: 'booking_date', header: 'Date', render: (r) => new Date(r.booking_date).toLocaleDateString() },
    { key: 'start_time', header: 'Start' },
    { key: 'end_time', header: 'End' },
    { key: 'purpose', header: 'Purpose' },
    { key: 'status', header: 'Status' },
    {
      key: 'actions',
      header: '',
      render: (r) =>
        r.status === 'CONFIRMED' ? (
          <button className="text-red-600 hover:underline text-sm" onClick={() => cancelBooking(r.id)}>
            Cancel
          </button>
        ) : null,
    },
  ];

  return (
    <div className="p-6 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Meeting Rooms</h1>
        <Button onClick={() => setShowForm(!showForm)}>{showForm ? 'Cancel' : 'Add Room'}</Button>
      </div>

      {showForm && (
        <form onSubmit={createRoom} className="bg-white rounded-lg shadow p-6 grid grid-cols-2 gap-4">
          <Input label="Code (max 6)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
          <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Input label="Capacity" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          <Input label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          <textarea
            className="col-span-2 border rounded-lg px-3 py-2"
            placeholder="Facilities"
            value={form.facilities}
            onChange={(e) => setForm({ ...form, facilities: e.target.value })}
          />
          <Button type="submit" className="col-span-2">
            Save Room
          </Button>
        </form>
      )}

      <Table columns={columns} rows={rooms} emptyMessage="No meeting rooms set up" />

      <div>
        <h2 className="text-lg font-semibold mb-3">My Booked Meeting Rooms</h2>
        <Table columns={bookingColumns} rows={myBookings} emptyMessage="No bookings yet" />
      </div>

      <Modal isOpen={!!bookingRoom} onClose={() => setBookingRoom(null)} title={`Book ${bookingRoom?.name}`}>
        <form onSubmit={book} className="space-y-4">
          <Input label="Date" type="date" value={bookingForm.bookingDate} onChange={(e) => setBookingForm({ ...bookingForm, bookingDate: e.target.value })} required />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Start Time" type="time" value={bookingForm.startTime} onChange={(e) => setBookingForm({ ...bookingForm, startTime: e.target.value })} required />
            <Input label="End Time" type="time" value={bookingForm.endTime} onChange={(e) => setBookingForm({ ...bookingForm, endTime: e.target.value })} required />
          </div>
          <Input label="Purpose" value={bookingForm.purpose} onChange={(e) => setBookingForm({ ...bookingForm, purpose: e.target.value })} />
          <Input label="Attendees" value={bookingForm.attendees} onChange={(e) => setBookingForm({ ...bookingForm, attendees: e.target.value })} placeholder="Names, comma separated" />
          <Button type="submit" className="w-full">
            Confirm Booking
          </Button>
        </form>
      </Modal>
    </div>
  );
}
